/**
 * English UI catalogue for namespace 'raw' (W2-05 easing-templates-raw): the Raw dock tab (DESIGN.md §4.18), its
 * events table, its commands, settings rows and help topic, and the reasons of the raw edit command body
 * (src/features/raw/rawEdit.ts).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

/** A line property id, as the Raw panel names it. */
const PROP =
  '{prop, select, moveX {Position X} moveY {Position Y} rotate {Rotation} alpha {Opacity} speed {Scroll speed} scaleX {Length / Size X} scaleY {Thickness / Size Y} color {Colour} text {Text} incline {Note tilt} paint {Brush} gif {GIF progress} other {{prop}}}';

const CONTROL = '{which, select, pos {X position} alpha {opacity} size {size} y {Y position} other {{which}}}';

export const m = defineMessages('raw', {
  panelTitle: 'Raw',
  regionLabel: 'Raw JSON of the selection',
  subtitle: 'Exactly what export writes for the selection',
  showLabel: 'Show',
  targetEvents: `${PROP} events · layer {layer} · {line}`,
  targetEventsExt: `${PROP} events · {line}`,
  targetNotes: '{count, plural, one {# note} other {# notes}} · {line}',
  targetLine: 'Line fields · {line}',
  targetControls: `Note control: ${CONTROL} · {line}`,
  emptyTitle: 'Nothing to show',
  emptyBody: 'Select notes, a keyframe or a segment, or make a line active to see the JSON export writes for it.',
  emptyAbsent: 'The export writes nothing for this selection any more.',
  controlAbsent: 'Not set: Phira uses a factor of 1. Write control points to set it.',
  editorLabel: 'RPE JSON',
  schemaOk: 'Schema: OK',
  schemaProblems: 'Schema: {count, plural, one {# problem} other {# problems}}',
  schemaWarnings: 'Schema: OK, {count, plural, one {# note} other {# notes}}',
  unchanged: 'Unchanged',
  edited: 'Edited',
  apply: 'Apply',
  applyHint: 'Apply (Ctrl+Enter)',
  revert: 'Revert',
  copy: 'Copy as RPE JSON',
  copied: 'Copied the RPE JSON of {target}.',
  copyFailed: 'The clipboard did not accept the text: {reason}',
  pretty: 'Pretty',
  compact: 'Compact',
  formatLabel: 'Layout',
  viewJson: 'JSON',
  viewTable: 'Events table',
  viewLabel: 'View',
  footnote:
    'Edits are read like an import, checked, lifted and applied as one undoable step. Nothing is repaired silently.',
  bakedNote:
    'Legacy speed mode writes eased speed as straight pieces. Applying this text replaces the curve with these pieces.',
  useCorrected: 'Show the corrected version',
  correctedHint: 'Phira would read this differently. Review the corrected text, then apply it.',
  goToProblem: 'Go to {path}',
  problemAt: '{path}: {message}',
  problemWhole: '{message}',
  problemsLabel: 'Problems',
  selectionChanged: 'The selection changed while you were editing.',
  showSelection: 'Discard edits and show the selection',
  docChanged: 'The chart changed since you started editing.',
  reload: 'Discard edits and reload',
  applied: 'Applied the raw JSON: {count, plural, =0 {nothing changed} one {# change} other {# changes}}.',
  appliedNothing: 'Nothing to apply: the JSON is what export already writes.',
  txApply: 'Edit raw JSON: {target}',
  // Events table
  tableLabel: 'Events of {target}',
  colIndex: '#',
  colStart: 'Start',
  colEnd: 'End',
  colFrom: 'From',
  colTo: 'To',
  colEasing: 'Easing',
  colSlice: 'Part',
  colBezier: 'Bezier',
  colLink: 'Link',
  colOrigin: 'Written for',
  sliceWhole: 'whole',
  bezierNone: 'no',
  linkNone: '-',
  origin:
    '{origin, select, segment {segment} single {single keyframe} trailing {jump at the end} lead {lead-in} padding {RPE padding} bakePiece {baked piece} other {{origin}}}',
  eventCount: '{count, plural, one {# event} other {# events}}',
  revealEvent: 'Go to the segment of event {index}',
  // Reasons (rawEdit.ts)
  eventSkipped:
    'Event {at} cannot be used: its time is invalid, it ends before it starts, or it starts before the previous event.',
  notesCount:
    'The list must keep exactly {count, plural, one {# note} other {# notes}}. Add or delete notes in the lane instead.',
  fileMissing: 'This project has no file named "{path}".',
  holdEnd: 'A Hold must end after it starts.',
  fatherInvalid: '"father" must be -1 (no parent) or the index of another line (0 to {max}).',
  groupInvalid: '"Group" {group} is not a line group of this chart.',
  rederived: '"isGif" and "numOfNotes" are worked out on export: edited values are not kept.',
  notRepresentable: 'This text cannot be applied: an empty list, or nothing a chart can hold.',
  kindMismatch: 'This text is for a different kind of item.',
  targetGone: 'The item this text belongs to no longer exists.',
  noProject: 'Open a project first.',
  noTarget: 'Select notes, a keyframe, a segment or a line first.',
  panelClosed: 'Open the Raw tab to edit JSON.',
  // Commands
  cmdShow: 'Show Raw JSON',
  cmdApply: 'Apply raw JSON',
  cmdRevert: 'Revert raw JSON',
  cmdCopy: 'Copy selection as RPE JSON',
  cmdFormat: 'Raw JSON: compact layout',
  // Settings
  settingCompact: 'Raw panel: compact JSON',
  settingCompactHelp: 'Write the JSON in the Raw panel on few lines, as export does, instead of indented.',
  settingShortest: 'Raw panel: shortest numbers',
  settingShortestHelp: 'Write whole numbers in decimal fields as short as possible (1) instead of like RPE (1.0).',
  // Help
  helpTitle: 'Raw JSON',
  helpBody:
    'The Raw tab (Full level) shows the RPE JSON that export writes for what you selected: a track\'s events, notes, a line\'s own fields or a note control curve. Edit it and press Ctrl+Enter to apply: the text is read like an import, every problem is listed with its place in the JSON, and nothing is repaired without you seeing it. The events table lists every event of a track with its easing, curve part, Bezier and link group; "Copy as RPE JSON" copies it for RPE or other tools.',
});

export default catalogOf(m);
